import React, { useEffect, useRef } from "react";
import "./VideoModal.css";
import projects from "../data/projects";

const VideoModal = ({ videoPath, project, onClose, onNavigate }) => {
  const modalRef = useRef(null);
  const videoRef = useRef(null);

  // Add keyboard event listeners for navigation and closing
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowRight") {
        onNavigate("next");
      } else if (e.key === "ArrowLeft") {
        onNavigate("prev");
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Auto-focus the modal for keyboard accessibility
    if (modalRef.current) {
      modalRef.current.focus();
    }

    // Prevent scrolling of the body when modal is open
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [onClose, onNavigate]);
  
  // Handle video playback when videoPath changes
  useEffect(() => {
    if (videoRef.current) {
      // Reset the video
      videoRef.current.load();
      // Auto-play video
      videoRef.current.play().catch((err) => {
        console.log("Autoplay prevented:", err);
      });
    }
  }, [videoPath]);

  // Use project data for title and description
  const projectTitle = project.title;
  const projectTech = project.tech;
  const projectDescription = project.description;


  return (
    <div
      className="video-modal-overlay"
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      tabIndex="-1"
      onClick={(e) => {
        // Close when clicking outside the video
        if (e.target === modalRef.current) {
          onClose();
        }
      }}
    >
      <div className="video-modal-content">
        <div className="video-modal-content-container">
          <div className="video-modal-header">
            <p className="video-modal-tech">{projectTech}</p>
            <h2 id="modal-title">{projectTitle}</h2>
            <div id="modal-description" className="video-modal-description" dangerouslySetInnerHTML={{ __html: projectDescription }}></div>
          </div>

          {/* Navigation buttons */}
          <div className="video-modal-nav-container">
            <button
              className="video-modal-nav-btn prev"
              onClick={() => onNavigate("prev")}
              aria-label="Previous project"
            >
              <span className="video-modal-nav-btn-icon"></span>
            </button>
            
            <div className="video-modal-nav-dots-container">
              {/* Navigation dots (mobile) */}
              <div className="video-modal-nav-dots mobile">
                {projects.map((p, index) => {
                  // Find the current project index
                  const currentIndex = projects.findIndex(proj => proj.id === project.id);
                  
                  return (
                    <button
                      key={index}
                      className={`video-modal-nav-dot ${p.id === project.id ? 'active' : ''}`}
                      onClick={() => {
                        // Pass the index directly to onNavigate
                        onNavigate(index);
                      }}
                      aria-label={`Go to project ${p.title}`}
                    />
                  );
                })}
              </div>
            </div>
            
            <button
              className="video-modal-nav-btn next"
              onClick={() => onNavigate("next")}
              aria-label="Next project"
            >
              <span className="video-modal-nav-btn-icon"></span>
            </button>
          </div>
        </div>

        <div className="video-container">
          <video 
            ref={videoRef} 
            muted
            loop
            autoPlay 
            playsInline
            key={videoPath} // Add key prop to force re-render when videoPath changes
            aria-label={`${projectTitle} project video`}
            aria-describedby="modal-description"
          >
            <source src={videoPath} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
      
      <button
        className="video-modal-close-btn"
        onClick={onClose}
        aria-label="Close video"
      >
        ✕
      </button>
    </div>
  );
};

export default VideoModal;
